import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addSite, editSite } from '../../../../api/endpoints/cmsSite';
import type { Site } from '../../../../api/types/cmsSite';
import { FormDialog } from '../../../../components/common';

const schema = z.object({
  company: z.string().min(1, 'Firma adı gerekli'),
  address: z.string().optional(),
  tursab: z.string().optional(),
  iata: z.string().optional(),
  keywords: z.string().optional(),
  description: z.string().optional(),
  googleMapsKey: z.string().optional(),
  googleTrackingCode: z.string().optional(),
  hotjarTrackingCode: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  company: '',
  address: '',
  tursab: '',
  iata: '',
  keywords: '',
  description: '',
  googleMapsKey: '',
  googleTrackingCode: '',
  hotjarTrackingCode: '',
};

function toFormValues(site: Site): FormValues {
  return {
    company: site.company ?? '',
    address: site.address ?? '',
    tursab: site.tursab ?? '',
    iata: site.iata ?? '',
    keywords: site.keywords ?? '',
    description: site.description ?? '',
    googleMapsKey: site.googleMapsKey ?? '',
    googleTrackingCode: site.googleTrackingCode ?? '',
    hotjarTrackingCode: site.hotjarTrackingCode ?? '',
  };
}

/** Create/edit dialog for Site Ayarları. Confirmed via local-backend swagger + live curl
 *  (see api/types/cmsSite.ts header). Deliberately sends ONLY the scalar fields modeled on
 *  `Site` — never round-trips a fetched row's nested nav-collections back into
 *  Add/EditSite (see api/types/cmsSite.ts's file header re: the runaway-payload risk). */
export function SiteFormDialog({
  open,
  site,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  site: Site | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!site;

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  useEffect(() => {
    if (open) reset(site ? toFormValues(site) : emptyValues);
  }, [open, site, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => (isEdit && site ? editSite({ id: site.id, ...values }) : addSite(values)),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Site güncellendi.' : 'Site oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'sites'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Siteyi Güncelle' : 'Yeni Site'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
    >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Firma"
                error={!!errors.company}
                helperText={errors.company?.message}
                {...register('company')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Adres" {...register('address')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="TURSAB" {...register('tursab')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="IATA" {...register('iata')} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Anahtar Kelimeler" {...register('keywords')} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" multiline rows={3} label="Açıklama" {...register('description')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Google Maps Key" {...register('googleMapsKey')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Google Tracking Code" {...register('googleTrackingCode')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Hotjar Tracking Code" {...register('hotjarTrackingCode')} />
            </Grid>
          </Grid>
    </FormDialog>
  );
}
